// src/components/aols/OLSstudent/OLSstudentProspect.tsx
// Updated to remove duplicate footer, ensuring single footer in PublicLayout.tsx
// Supports Sun Light Meditation education lead generation
// Removed Location Pre-evaluation section to avoid duplication with LocationIndicator.tsx
// Removed unused imports for CalendarOutlined, TeamOutlined, and StarOutlined
// 06/03/25 08:06 PM +07
import React, { useState, useEffect, Suspense } from "react";
import { Layout, Typography, Button } from "antd";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
  GoogleOutlined,
  TwitterOutlined,
  YoutubeOutlined,
  InstagramOutlined,
  FacebookOutlined,
  LinkedinOutlined,
  GlobalOutlined,
  BookOutlined,
  UserAddOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";
import GoogleMap from "../../dev/GoogleMap/GoogleMap";
import LocationIndicator from "./LocationIndicator";
import { publicImages } from "../../auth/constants/PublicImg";
import "./OLSstudentProspect.css";

const { Title, Text } = Typography;
const { Header, Content } = Layout;

const EstOLSLM = React.lazy(() => import("../../auth/components/EstOLSLM"));

// Type Definitions
interface Dimensions {
  width: number;
  height: number;
}

interface PioneerDiscount {
  level: string;
  discount: string;
}

interface Perk {
  platform: string;
  icon: React.ReactNode;
  credits: string | number;
}

interface PerkCategory {
  category: string;
  className: string;
  perks: Perk[];
}

interface OnboardingStep {
  step: string;
}

interface PioneerBenefit {
  benefit: string;
}

const OLSStudentProspect: React.FC = () => {
  const [dimensions, setDimensions] = useState<Dimensions>({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const [isMapOpen, setIsMapOpen] = useState<boolean>(false);

  useEffect(() => {
    const handleResize = () => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const toggleMap = () => {
    setIsMapOpen((prev) => !prev);
  };

  const pioneerDiscounts: PioneerDiscount[] = [
    { level: "1st in the City, State, Country, Continent", discount: "40% - 50%" },
    { level: "2nd in the City, State, Country, Continent", discount: "30% - 40%" },
    { level: "3rd in the City, State, Country, Continent", discount: "30% - 20%" },
  ];

  const perkCategories: PerkCategory[] = [
    {
      category: "Reviews",
      className: "reviews",
      perks: [
        { platform: "Google", icon: <GoogleOutlined />, credits: 4 },
        { platform: "Lessons", icon: <BookOutlined />, credits: 2 },
      ],
    },
    {
      category: "Social Media",
      className: "social-media",
      perks: [
        { platform: "YouTube", icon: <YoutubeOutlined />, credits: 3 },
        { platform: "Instagram (active OLS members only)", icon: <InstagramOutlined />, credits: 1 },
        { platform: "Twitter", icon: <TwitterOutlined />, credits: 4 },
        { platform: "LinkedIn", icon: <LinkedinOutlined />, credits: 5 },
        { platform: "Facebook pages", icon: <FacebookOutlined />, credits: 10 },
      ],
    },
    {
      category: "Subscriptions",
      className: "subscriptions",
      perks: [
        { platform: "Odysee", icon: <GlobalOutlined />, credits: 5 },
        { platform: "Blog", icon: <BookOutlined />, credits: 3 },
        { platform: "CrunchBase", icon: <GlobalOutlined />, credits: 3 },
        { platform: "Owler and Weigh in", icon: <GlobalOutlined />, credits: 3 },
      ],
    },
    {
      category: "Courses",
      className: "courses",
      perks: [
        { platform: "Udemy prep class", icon: <BookOutlined />, credits: 400 },
      ],
    },
    {
      category: "Referrals",
      className: "referrals",
      perks: [
        { platform: "Recommend a new prospect (10% referral)", icon: <UserAddOutlined />, credits: "10%" },
      ],
    },
    {
      category: "Crypto",
      className: "crypto",
      perks: [
        { platform: "Latium", icon: <GlobalOutlined />, credits: 20 },
        { platform: "Mine Pi digital currencies (use username: asvitloaten)", icon: <GlobalOutlined />, credits: 10 },
        { platform: "Apply valid cryptocurrencies for OLS service fees", icon: <GlobalOutlined />, credits: 10 },
      ],
    },
    {
      category: "Content Creation",
      className: "content-creation",
      perks: [
        { platform: "Create and share a video on OLS experience", icon: <VideoCameraOutlined />, credits: 20 },
      ],
    },
  ];

  const onboardingSteps: string[] = [
    "OLS Student Evaluation Education Tuition Application Register",
    "Receive your assessment results via email from intro@olsme.com",
    "Submit 50% pre-payment for OLS Intro Service",
    "Schedule your OLS Intro video call session",
    "Attend the OLS Intro session",
    "Submit 50% pre-payment for OLS Initi Service",
    "Schedule your OLS Initi session",
    "Attend the OLS Initi session",
    "Welcome aboard! You’re an OLS student",
  ];

  const pioneerBenefits: string[] = [
    "Leveled discounted tuition fees",
    "Priority access to OLS services",
    "Exclusive additional credits perks",
    "Be the first one who will unlock self-potential",
    "Earn real assets equity Light minutes with Certified Portfolio",
    "Exclusive Build Develop your Light Age and Light Body",
  ];

  const testimonialImage = publicImages.getImageById(11);

  return (
    <div className="styled-layout">
      <Helmet>
        <title>OLS Student Prospect | Sun Light Meditation Education - OneLightSystem</title>
        <meta
          name="description"
          content="Join OneLightSystem OLS to explore Sun Light Meditation education. Discover pioneer discounts, scholarships, tuition fees, and calculate savings with our OLSLM estimator."
        />
        <meta
          name="description"
          content="Join OLSME sessions at 40:00–45:00 Egypt time. Register at olsme.com/register"
        />
        <meta
          name="keywords"
          content="OLS student prospect, Sun Light Meditation, meditation education, OLS pioneer program, tuition fees, OLSLM estimator, Light Civilization, OneLightSystem OLS, scholarships"
        />
        <meta name="author" content="OneLightSystem OLS" />
        <meta name="robots" content="index, follow" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta property="og:title" content="OLS Student Prospect | Sun Light Meditation Education - OneLightSystem" />
        <meta
          property="og:description"
          content="Join OneLightSystem OLS to explore Sun Light Meditation education, pioneer benefits, scholarships, and tuition options. Calculate your meditation savings today."
        />
        <meta property="og:url" content="https://www.olsme.com/aols/ols-student-prospect" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.olsme.com/logo.jpg" />
        <meta property="og:image:alt" content="OneLightSystem OLS Logo" />
        <meta property="og:site_name" content="OneLightSystem OLS" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="OLS Student Prospect | Sun Light Meditation Education - OneLightSystem" />
        <meta
          name="twitter:description"
          content="Join OneLightSystem OLS to explore Sun Light Meditation education, pioneer benefits, scholarships, and tuition options."
        />
        <meta name="twitter:image" content="https://www.olsme.com/logo.jpg" />
        <meta name="twitter:image:alt" content="OneLightSystem OLS Logo" />
        <meta name="twitter:site" content="@onelightsystem" />
        <meta name="twitter:creator" content="@onelightsystem" />
        <link rel="canonical" href="https://www.olsme.com/aols/ols-student-prospect" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "OLS Student Prospect",
            description:
              "Join OneLightSystem OLS to explore Sun Light Meditation education, pioneer benefits, scholarships, and tuition options.",
            url: "https://www.olsme.com/aols/ols-student-prospect",
            breadcrumb: {
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "Home",
                  item: "https://www.olsme.com/",
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "AOLS",
                  item: "https://www.olsme.com/aols",
                },
                {
                  "@type": "ListItem",
                  position: 3,
                  name: "OLS Student Pioneer Program",
                  item: "https://www.olsme.com/aols/ols-student-prospect",
                },
              ],
            },
            publisher: {
              "@type": "EducationalOrganization",
              name: "OneLightSystem OLS",
              description:
                "OneLightSystem OLS provides Sun Light Meditation education with pioneer discounts, scholarships, and tuition fee options.",
              url: "https://www.olsme.com",
              logo: "https://www.olsme.com/logo.jpg",
              contactPoint: {
                "@type": "ContactPoint",
                email: "intro@olsme.com",
                contactType: "Customer Service",
              },
              address: {
                "@type": "PostalAddress",
                addressLocality: "Belmont",
                addressRegion: "CA",
                addressCountry: "US",
              },
              founder: {
                "@type": "Person",
                name: "Nazar Pankiv",
                alternateName: "Asvitloaten",
              },
              sameAs: [
                "https://www.olsme.com",
                "https://www.ols-med.net",
                "https://twitter.com/onelightsystem",
                "https://www.facebook.com/onelightsystemols",
                "https://www.linkedin.com/company/one-light-system",
                "https://www.youtube.com/@ONELIGHTSYSTEMOLSMeditation",
              ],
            },
            mainEntity: {
              "@type": "Offer",
              name: "OLS Student Program",
              description:
                "Meditation education with pioneer discounts, scholarships, and tuition fee pre-evaluation.",
              url: "https://www.olsme.com/register/olssregister",
            },
          })}
        </script>
      </Helmet>

      <header className="styled-header">
        <Link className="logo-link" to="/login" aria-label="Return to login page">
          <img
            className="logo-image"
            src="/logo.jpg"
            alt="OneLightSystem OLS Logo"
            aria-label="OLS Logo"
            loading="lazy"
          />
        </Link>
        <Title level={2} style={{ color: "#ffffff", margin: 0, fontWeight: 600 }}>
          onelightsystem OLS iee.aeo
        </Title>
      </header>

      <Layout className="inner-layout">
        <div className="styled-content">
          <div className="section-container common-info">
            <Title level={2} style={{ color: "#ffffff" }}>
              OLS Student Portal
            </Title>
            <Text style={{ color: "#ffffff" }}>
              Start your journey with OneLightSystem OLS. Learn Sun Light Meditation, access exclusive resources, and join our Light Civilization community.
            </Text>
            <div style={{ margin: "20px 0" }}>
              <Button className="styled-button">
                <Link to="/lightScience" aria-label="Learn more about OLS">
                  About OLS
                </Link>
              </Button>
              <Button className="styled-button">
                <Link to="/Stat" aria-label="View OLS student statistics">
                  OLS Student
                </Link>
              </Button>
              <Button className="styled-button">
                <Link to="/register/olssregister" aria-label="Register as an OLS student">
                  Register Now
                </Link>
              </Button>
              <Button className="styled-button">
                <Link to="/OLSsPrivacy" aria-label="View OLS student disclosure">
                  OLS Student Disclosure
                </Link>
              </Button>
              <Button className="styled-button">
                <Link to="/contact" aria-label="Contact OLS support">
                  Contact Us
                </Link>
              </Button>
            </div>
          </div>

          <div className="modern-frame">
            <Suspense fallback={<div>Loading Estimator...</div>}>
              <EstOLSLM />
            </Suspense>
          </div>

          <div className="section-container">
            <Title level={2} style={{ color: "#ffffff" }}>
              Become an OLS Student
            </Title>
            <Text style={{ color: "#ffffff" }}>
              Enroll today to build your Light Field and gain transformative meditation skills with OLS!
            </Text>
            <div className="timeline-container" style={{ maxWidth: dimensions.width < 768 ? "400px" : "600px" }}>
              <Title level={4} style={{ color: "#ffffff", textAlign: "center", marginBottom: "30px" }}>
                Onboarding Process
              </Title>
              {onboardingSteps.map((step, index) => (
                <div className="timeline-item" key={index}>
                  <div className="timeline-dot">{index + 1}</div>
                  <div className="timeline-line"></div>
                  <div className="timeline-content">{step}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="section-container">
            <Title level={2} className="pioneer-title">
              OLS Student Pioneer Program
            </Title>
            <Title level={4} className="centered-subtitle">
              Be a pioneer in your region and enjoy exclusive tuition discounts and benefits:
            </Title>
            <ul className="custom-list">
              {pioneerBenefits.map((benefit, index) => (
                <li className="custom-list-item" key={index}>{benefit}</li>
              ))}
            </ul>
            <Title level={3} style={{ color: "#ffffff", marginTop: 20 }}>
              Pioneer Leveled Discounts
            </Title>
            <Text style={{ color: "#ffffff", marginBottom: 10 }}>
              Early adopters receive tuition discounts based on their pioneer status:
            </Text>
            <div className="discount-cards-container">
              {pioneerDiscounts.map((discount) => {
                const [number, rest] = discount.level.split(" in ");
                return (
                  <div
                    className={`discount-card ${discount.discount === "40% - 50%" ? "high-discount" : ""}`}
                    key={discount.level}
                  >
                    <div className="discount-card-title">
                      <span className="discount-card-number">{number}</span>
                      <span>in {rest}</span>
                    </div>
                    <div className="discount-card-discount">{discount.discount}</div>
                  </div>
                );
              })}
            </div>
            <Title level={3} className="perks-title">
              Additional Perks (Credits)
            </Title>
            <Text style={{ color: "#ffffff", marginBottom: 10 }}>
              Earn credits by engaging with OLS:
            </Text>
            <div className="perks-container">
              {perkCategories.map((category) => (
                <div key={category.category} className={`perk-category ${category.className}`}>
                  <span className="category-title">{category.category}:</span>
                  {category.perks.map((perk, index) => (
                    <span key={perk.platform} className="perk-item">
                      {perk.icon} {perk.platform} (<span className="credit">{perk.credits}</span>)
                      {index < category.perks.length - 1 && ","}
                    </span>
                  ))}
                </div>
              ))}
            </div>
            <Title level={3} style={{ color: "#ffffff", marginTop: 20 }}>
              OLS Scholarship Program
            </Title>
            <Title level={4} className="scholarship-title">
              <span>Unlock Your Potential with Our Exclusive Scholarship Opportunities</span>
              <span role="img" aria-label="Graduation Cap">🧑‍🎓</span>
            </Title>
            <Text className="scholarship-description">
              We are proud to offer scholarships for individuals who demonstrate advanced characteristics, multiple achievements, an excellent bio, and numerous reference recommendations. The scholar should participate in OLS iee.aeo undergraduate departments. Select this option during registration and include all necessary information for evaluation to qualify for a tuition discount.
            </Text>
            <Button className="styled-button">
              <Link to="/contact" aria-label="Contact OLS for scholarship inquiries">
                Contact
              </Link>
            </Button>
          </div>

          <img
            className="testimonial-image"
            src={testimonialImage.url}
            alt={testimonialImage.alt}
            title={testimonialImage.title}
            loading="lazy"
          />

          <LocationIndicator />

          <div className="section-container important-info-section">
            <Title level={2} style={{ color: "#ffffff" }}>
              Important Information for OLS Students
            </Title>
            <Title level={4} style={{ color: "#ffffff", marginTop: 20 }}>
              Tuition Fee Conditions
            </Title>
            <Text style={{ color: "#ffffff" }}>
              Fees are updated quarterly based on global conditions and student numbers.
            </Text>
            <Text style={{ color: "#ffffff", display: "block", marginTop: 10 }}>
              Choose "Fast Onboarding" with pre-evaluated general fees for quicker access (income $100k./year).
            </Text>
            <Title level={4} style={{ color: "#ffffff", marginTop: 20 }}>
              OLS Student Support
            </Title>
            <Text style={{ color: "#ffffff" }}>
              Free support from Asvitloaten is included for active students (first-come, first-served).
            </Text>
            <Title level={4} style={{ color: "#ffffff", marginTop: 20 }}>
              Transparency
            </Title>
            <Text style={{ color: "#ffffff" }}>
              OLS operates transparently—no hidden fees. Report issues to: intro@olsme.com.
            </Text>
          </div>

          <div style={{ margin: "40px 0 20px 0" }}>
            <Button className="styled-button">
              <Link to="/privacy-one" aria-label="View OLS student disclosure">
                OLS Student Disclosure
              </Link>
            </Button>
            <Button className="styled-button">
              <Link to="/lightScience" aria-label="Learn more about OLS">
                About OLS
              </Link>
            </Button>
            <Button className="styled-button">
              <Link to="/contact" aria-label="Contact OLS support">
                Contact Us
              </Link>
            </Button>
            <Button className="styled-button">
              <Link to="/register/olssregister" aria-label="Register as an OLS student">
                Register Now
              </Link>
            </Button>
          </div>

          <div className="map-section">
            <Button
              type="primary"
              onClick={toggleMap}
              style={{ marginTop: 20, background: "#3552fc", border: "none" }}
              aria-label={isMapOpen ? "Hide OLS locations map" : "Show OLS locations map"}
            >
              {isMapOpen ? "Hide Map" : "Show Map"}
            </Button>
            {isMapOpen && (
              <GoogleMap
                mapUrl="https://www.google.com/maps/d/embed?mid=1IeLInyV7cTtRSFS6fZj-VrbsQkA&ehbc=2E312F"
                title="OneLightSystem OLS Locations"
                ariaLabel="Interactive map of OneLightSystem OLS locations"
              />
            )}
          </div>
        </div>
      </Layout>
      {/* Removed <Footer /> to prevent duplication; footer is rendered in PublicLayout.tsx */}
    </div>
  );
};

export default OLSStudentProspect;